import type { NextPage } from 'next'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Header } from '@/components/Header'
import { ListSkeleton } from '@/components/ListSkeleton'
import { getNews } from '@/utils/ondemand'

type TStory = {
  newsID: number
  headline: string
  preview: string
  organization: string
  displayTime: string
  canonicalUrl: string
  publishDate: string
  imageURL: string
}

dayjs.extend(utc)

const News: NextPage = () => {
  const [news, setNews] = useState<TStory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  const fetchMoreNews = () => {
    setIsFetchingMore(true)

    const lastStory = news[news.length - 1]
    const lastStoryPublishedDate = dayjs(
      lastStory.publishDate,
      'YYYY-MM-DD HH:mm:ss Z'
    )

    getNews(15, lastStoryPublishedDate.utc().format('YYYYMMDDHHmmss')).then(
      response => {
        const ids = news?.map(story => story.newsID)
        const newNews = response.filter(
          (newStory: TStory) => ids.indexOf(newStory.newsID) === -1
        )
        setNews(news.concat(newNews))
        setIsFetchingMore(false)
      }
    )
  }

  useEffect(() => {
    getNews(20).then(news => {
      setNews(news)
      setIsLoading(false)
    })
  }, [])

  return (
    <>
      <Header title="News" />
      <div className="px-4 pb-6 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-gray-900">News</h1>
          </div>
        </div>
        <div className="my-8 flex flex-col">
          {isLoading ? (
            <ListSkeleton />
          ) : (
            <InfiniteScroll
              dataLength={news?.length}
              next={() => fetchMoreNews()}
              hasMore={!isFetchingMore}
              loader={<div className="mt-5 text-center">Loading more...</div>}
            >
              <ul role="list" className="divide-y divide-gray-200">
                {news.map(story => (
                  <li
                    key={story.newsID}
                    className="relative bg-white px-4 py-5 hover:bg-gray-50"
                  >
                    <div className="relative flex items-center space-x-3">
                      {story?.imageURL && (
                        <div className="flex-shrink-0">
                          <Image
                            src={story?.imageURL}
                            alt="story"
                            width={128}
                            height={96}
                            style={{
                              maxWidth: '100%',
                              height: '96px'
                            }}
                          />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex justify-between space-x-3">
                          <div className="min-w-0 flex-1">
                            <a
                              href={story.canonicalUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block focus:outline-none"
                            >
                              <span
                                className="absolute inset-0"
                                aria-hidden="true"
                              />
                              <p className="text-md truncate font-bold text-black">
                                {story.headline}
                              </p>
                              <p className="truncate text-sm text-gray-500">
                                {story.organization}
                              </p>
                            </a>
                          </div>
                          <p className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
                            {story.displayTime}
                          </p>
                        </div>
                        <div className="mt-1">
                          <p className="line-clamp-2 text-sm text-gray-600">
                            {story.preview}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </>
  )
}

export default News
